import React from 'react';
import { AlertCircle, AlertTriangle, Clock, Info, ChevronDown, ChevronRight, User, Users, Search } from 'lucide-react';
import { Alert, AlertLevel } from '../../types/ltss';

const ALERT_CONFIG: Record<AlertLevel, {
  bg: string;
  border: string;
  badge: string;
  icon: React.ElementType;
}> = {
  CRITICAL: { 
    bg: 'bg-red-500/10', 
    border: 'border-red-500/30', 
    badge: 'bg-red-500',
    icon: AlertCircle 
  },
  WARNING: { 
    bg: 'bg-orange-500/10', 
    border: 'border-orange-500/30', 
    badge: 'bg-orange-500',
    icon: AlertTriangle 
  },
  CAUTION: { 
    bg: 'bg-yellow-500/10', 
    border: 'border-yellow-500/30', 
    badge: 'bg-yellow-500',
    icon: Clock 
  },
  INFO: { 
    bg: 'bg-blue-500/10', 
    border: 'border-blue-500/30', 
    badge: 'bg-blue-500',
    icon: Info 
  },
};

interface AlertItemProps {
  alert: Alert;
  isExpanded: boolean;
  onToggle: () => void;
  onSearchPerson?: (name: string, type: 'staff' | 'client') => void;
}

export function AlertItem({ alert, isExpanded, onToggle, onSearchPerson }: AlertItemProps) {
  const config = ALERT_CONFIG[alert.type];
  const Icon = config.icon;

  const handleSearchClick = (e: React.MouseEvent, name: string, type: 'staff' | 'client') => {
    e.stopPropagation();
    if (onSearchPerson) {
      onSearchPerson(name, type);
    }
  };

  return (
    <div
      onClick={onToggle}
      className={`p-4 border-l-4 ${config.border} ${config.bg} cursor-pointer hover:bg-opacity-20 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.badge}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.badge} text-white`}>
              {alert.category}
            </span>
            {(alert.staffName || alert.clientName) && (
              <span className="text-xs text-slate-500">•</span>
            )}
            {alert.staffName && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <User className="w-3 h-3" />
                {alert.staffName}
              </span>
            )}
            {alert.clientName && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {alert.clientName}
              </span>
            )}
            <div className="flex-1" />
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </div>
          <p className="text-sm font-medium text-slate-200">{alert.message}</p>
          
          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-3 space-y-2">
              {alert.details && (
                <p className="text-xs text-slate-400">{alert.details}</p>
              )}
              
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {alert.staffName && onSearchPerson && (
                  <button
                    onClick={(e) => handleSearchClick(e, alert.staffName!, 'staff')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium 
                             bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 
                             transition-colors"
                  >
                    <Search className="w-3 h-3" />
                    View {alert.staffName}'s Activity
                  </button>
                )}
                {alert.clientName && onSearchPerson && (
                  <button
                    onClick={(e) => handleSearchClick(e, alert.clientName!, 'client')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium 
                             bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 
                             transition-colors"
                  >
                    <Search className="w-3 h-3" />
                    View {alert.clientName}'s Activity
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
